// Локальна база на пристрої (IndexedDB) для офлайн-режиму.
// Зберігає весь пакет питань (з правильними відповідями!), категорії, кеш /me,
// а також чергу проходжень, зроблених офлайн (outbox → досилаємо на сервер).
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type OfflineAnswer = { id: number; textUk: string; isCorrect: boolean; order: number };
export type OfflineQuestion = {
  id: number;
  categoryId: number;
  textUk: string;
  imageUrl: string | null;
  explanation: string | null;
  explanationCasual?: string | null;
  difficulty: number;
  order: number;
  licenseCategories: string[];
  answers: OfflineAnswer[];
};
export type OfflineCategory = {
  id: number;
  slug: string;
  nameUk: string;
  iconUrl: string | null;
  order: number;
};
export type OfflinePack = {
  version: string;
  generatedAt: string;
  counts: { questions: number; categories: number };
  categories: OfflineCategory[];
  questions: OfflineQuestion[];
};

// Одне офлайн-проходження в черзі на синхронізацію
export type OutboxAttempt = {
  id: string; // uuid — ключ ідемпотентності (стане Attempt.id на сервері)
  mode: string;
  categoryId: number | null;
  total: number;
  startedAt: string; // ISO
  finishedAt: string; // ISO
  answers: Array<{ questionId: number; answerId: number | null; timeMs: number }>;
  // локальний результат (для показу без сервера)
  score: number;
  passed: boolean;
  xpEarned: number;
  createdAt: number;
};

interface PdrDb extends DBSchema {
  meta: { key: string; value: { key: string; value: unknown } };
  questions: { key: number; value: OfflineQuestion; indexes: { byCategory: number } };
  categories: { key: number; value: OfflineCategory };
  outbox: { key: string; value: OutboxAttempt };
}

let dbp: Promise<IDBPDatabase<PdrDb>> | null = null;

function db(): Promise<IDBPDatabase<PdrDb>> {
  if (!dbp) {
    dbp = openDB<PdrDb>('pdr-offline', 1, {
      upgrade(d) {
        d.createObjectStore('meta', { keyPath: 'key' });
        const q = d.createObjectStore('questions', { keyPath: 'id' });
        q.createIndex('byCategory', 'categoryId');
        d.createObjectStore('categories', { keyPath: 'id' });
        d.createObjectStore('outbox', { keyPath: 'id' });
      },
    });
  }
  return dbp;
}

// ===== meta (key/value) =====
export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  const row = await (await db()).get('meta', key);
  return row?.value as T | undefined;
}
export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', { key, value });
}

// ===== пакет питань =====
export async function savePack(pack: OfflinePack): Promise<void> {
  const d = await db();
  const tx = d.transaction(['questions', 'categories', 'meta'], 'readwrite');
  await tx.objectStore('questions').clear();
  await tx.objectStore('categories').clear();
  for (const q of pack.questions) await tx.objectStore('questions').put(q);
  for (const c of pack.categories) await tx.objectStore('categories').put(c);
  await tx.objectStore('meta').put({ key: 'packVersion', value: pack.version });
  await tx.objectStore('meta').put({ key: 'packGeneratedAt', value: pack.generatedAt });
  await tx.done;
}

export async function getPackVersion(): Promise<string | null> {
  return (await getMeta<string>('packVersion')) ?? null;
}

export async function isPackReady(): Promise<boolean> {
  const d = await db();
  const n = await d.count('questions');
  return n > 0;
}

export async function allQuestions(): Promise<OfflineQuestion[]> {
  return (await db()).getAll('questions');
}
export async function allCategories(): Promise<OfflineCategory[]> {
  const cats = await (await db()).getAll('categories');
  return cats.sort((a, b) => a.order - b.order);
}
export async function questionById(id: number): Promise<OfflineQuestion | undefined> {
  return (await db()).get('questions', id);
}

// Категорії з пакета + кількість питань у кожній (для сторінки «Вчи по темах» офлайн).
// Порожні ховаємо — як робить серверний GET /categories.
export async function offlineCategoriesWithCount(): Promise<
  Array<OfflineCategory & { questionCount: number }>
> {
  const cats = await allCategories();
  const qs = await allQuestions();
  const counts = new Map<number, number>();
  for (const q of qs) counts.set(q.categoryId, (counts.get(q.categoryId) ?? 0) + 1);
  return cats
    .map((c) => ({ ...c, questionCount: counts.get(c.id) ?? 0 }))
    .filter((c) => c.questionCount > 0);
}

// ===== outbox (черга синхронізації) =====
export async function enqueueAttempt(a: OutboxAttempt): Promise<void> {
  await (await db()).put('outbox', a);
}
export async function getOutbox(): Promise<OutboxAttempt[]> {
  return (await db()).getAll('outbox');
}
export async function removeFromOutbox(id: string): Promise<void> {
  await (await db()).delete('outbox', id);
}
export async function outboxCount(): Promise<number> {
  return (await db()).count('outbox');
}
